<template>
    <el-dialog v-model="visible" title="AI 标签词表" width="860px" destroy-on-close @open="load">
        <el-alert type="info" :closable="false" show-icon>
            这里只控制 AI 能看到哪些标签，不会删除标签、修改图片引用，也不会影响玩家已有拼图。
        </el-alert>

        <div class="toolbar">
            <el-select v-model="criteria.scope" style="width: 140px">
                <el-option label="已上线图片" value="online" />
                <el-option label="有引用图片" value="referenced" />
            </el-select>
            <el-input-number v-model="criteria.min_references" :min="1" :max="10000" controls-position="right" />
            <span class="hint">最少引用次数</span>
            <el-input-number v-model="criteria.limit_per_library" :min="1" :max="500" controls-position="right" />
            <span class="hint">每类最多候选</span>
            <el-button type="primary" plain :loading="loading" @click="suggest(true)">按使用量生成建议</el-button>
        </div>

        <div class="summary">
            当前模式：{{ policy.mode === 'configured' ? `已配置（版本 ${policy.version}）` : '尚未配置，AI 暂时会看到全部标签' }}。
            本次选择：主要 {{ selected.major_tags.length }}、次要 {{ selected.minor_tags.length }}、辅助 {{ selected.assistant_tags.length }}。
        </div>

        <el-tabs v-model="activeTab" class="vocabulary-tabs" v-loading="loading">
            <el-tab-pane v-for="field in fields" :key="field.key" :name="field.key"
                         :label="`${field.label}（${selected[field.key].length}/${rows[field.key].length}）`">
                <div class="selection-actions">
                    <el-button size="small" text type="primary" @click="selectAll(field.key)">全选候选</el-button>
                    <el-button size="small" text @click="selected[field.key] = []">清空</el-button>
                </div>
                <el-scrollbar height="380px">
                    <div v-if="!rows[field.key].length" class="empty">暂无符合条件的候选标签</div>
                    <el-checkbox-group v-else v-model="selected[field.key]" class="tag-grid">
                        <el-checkbox v-for="item in rows[field.key]" :key="item.id" :label="item.id" border>
                            <span>{{ item.name }}</span>
                            <small v-if="item.reference_count !== null">引用 {{ item.reference_count }}</small>
                        </el-checkbox>
                    </el-checkbox-group>
                </el-scrollbar>
            </el-tab-pane>
        </el-tabs>

        <template #footer>
            <el-button @click="visible = false">取消</el-button>
            <el-button type="primary" :loading="saving" :disabled="!canSave" @click="save">
                保存为 AI 词表
            </el-button>
        </template>
    </el-dialog>
</template>

<script>
import axios from 'ts-axios-new';
import {ElMessage, ElMessageBox} from 'element-plus';

const EMPTY_SELECTION = () => ({major_tags: [], minor_tags: [], assistant_tags: []});
const EMPTY_ROWS = () => ({major_tags: [], minor_tags: [], assistant_tags: []});

export default {
    name: 'AiVocabularyDialog',
    props: {modelValue: {type: Boolean, default: false}},
    emits: ['update:modelValue'],
    data() {
        return {
            loading: false,
            saving: false,
            activeTab: 'major_tags',
            policy: {mode: 'legacy_all', version: 0},
            criteria: {scope: 'online', min_references: 20, limit_per_library: 100},
            rows: EMPTY_ROWS(),
            selected: EMPTY_SELECTION(),
            fields: [
                {key: 'major_tags', label: '主要标签'},
                {key: 'minor_tags', label: '次要标签'},
                {key: 'assistant_tags', label: '辅助标签'},
            ],
        };
    },
    computed: {
        visible: {
            get() { return this.modelValue; },
            set(value) { this.$emit('update:modelValue', value); },
        },
        canSave() {
            return this.fields.every(field => this.selected[field.key].length > 0);
        },
    },
    methods: {
        errorMessage(error, fallback) {
            return error?.response?.data?.status?.message || error?.response?.data?.message || error?.message || fallback;
        },
        async load() {
            this.loading = true;
            try {
                const response = await axios.get('/cms/v1/ai/vocabulary');
                this.policy = response.data.data;
                if (this.policy.mode === 'configured') {
                    this.selected = {
                        major_tags: [...(this.policy.major_tag_ids || [])],
                        minor_tags: [...(this.policy.minor_tag_ids || [])],
                        assistant_tags: [...(this.policy.assistant_tag_ids || [])],
                    };
                } else {
                    this.selected = EMPTY_SELECTION();
                }
                await this.suggest(this.policy.mode !== 'configured', false);
            } catch (error) {
                ElMessage.error(this.errorMessage(error, '加载 AI 词表失败'));
            } finally {
                this.loading = false;
            }
        },
        mergeConfiguredRows(field, suggestedRows) {
            const rows = [...suggestedRows];
            if (this.policy.mode !== 'configured') return rows;
            const known = new Set(rows.map(item => item.id));
            const ids = this.policy[field.replace('_tags', '_tag_ids')] || [];
            const names = this.policy[field] || [];
            ids.forEach((id, index) => {
                if (!known.has(id)) rows.push({id, name: names[index] || id, reference_count: null});
            });
            return rows;
        },
        async suggest(resetSelection = true, manageLoading = true) {
            if (manageLoading) this.loading = true;
            try {
                const response = await axios.post('/cms/v1/ai/vocabulary/suggest', this.criteria);
                const data = response.data.data;
                this.fields.forEach(field => {
                    const key = field.key;
                    this.rows[key] = this.mergeConfiguredRows(key, data[key] || []);
                    if (resetSelection) this.selected[key] = (data[key] || []).map(item => item.id);
                });
                if (resetSelection) ElMessage.success('已按实际使用量生成候选，请审核后保存');
            } catch (error) {
                ElMessage.error(this.errorMessage(error, '生成 AI 词表建议失败'));
            } finally {
                if (manageLoading) this.loading = false;
            }
        },
        selectAll(field) {
            this.selected[field] = this.rows[field].map(item => item.id);
        },
        async save() {
            if (!this.canSave) return;
            try {
                await ElMessageBox.confirm(
                    `保存后 AI 只会从已选的 ${this.selected.major_tags.length + this.selected.minor_tags.length + this.selected.assistant_tags.length} 个标签中选择。标签库和图片引用不会改变。`,
                    '确认 AI 词表',
                    {type: 'warning', confirmButtonText: '保存', cancelButtonText: '取消'},
                );
            } catch (_) {
                return;
            }
            this.saving = true;
            try {
                const response = await axios.put('/cms/v1/ai/vocabulary', {
                    expected_version: this.policy.version,
                    ...this.selected,
                });
                this.policy = response.data.data;
                ElMessage.success(`AI 词表已保存为版本 ${this.policy.version}`);
                this.visible = false;
            } catch (error) {
                ElMessage.error(this.errorMessage(error, '保存 AI 词表失败，请重新打开后再试'));
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.toolbar { display: flex; align-items: center; gap: 10px; margin: 16px 0 8px; flex-wrap: wrap; }
.hint { color: var(--el-text-color-secondary); font-size: 12px; margin-left: -6px; }
.summary { color: var(--el-text-color-regular); font-size: 13px; margin-bottom: 8px; }
.selection-actions { margin-bottom: 10px; }
.tag-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.tag-grid .el-checkbox { margin: 0; width: 100%; }
.tag-grid small { color: var(--el-text-color-secondary); margin-left: 8px; }
.empty { color: var(--el-text-color-secondary); text-align: center; padding: 80px 0; }
</style>
